import { useState, useEffect, FC } from 'react';
import {
  BarChart3, Users,
  AlertCircle, Building2, Briefcase, UserCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';

interface CompanyStats {
  totalCompanies: number;
  activeCompanies: number;
  totalJobs: number;
  totalCandidates: number;
  totalMatches: number;
  totalApplications?: number;
  jobsByType: {
    type: string;
    count: number;
  }[];
  matchesByScore: {
    score: string;
    count: number;
  }[];
  matchTrend: {
    date: string;
    matches: number;
  }[];
}

interface Job {
  id: string;
  type: string;
  matches: { score: number }[];
}

interface Company {
  jobs: Job[];
}

type TimeRange = '24h' | '7d' | '30d' | '90d';

export const StatisticsPage: FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [loading, setLoading] = useState(true);
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch comprehensive statistics using parallel queries
      const [
        { data: companies },
        { count: totalCandidates },
        { count: totalApplications },
        jobTypes,
        matchScores,
        matchTrend
      ] = await Promise.all([
        supabase
          .from('companies')
          .select(`
            *,
            jobs (
              id,
              type,
              matches (
                score
              )
            )
          `),
        supabase
          .from('candidates')
          .select('*', { count: 'exact' }),
        supabase
          .from('job_applications')
          .select('*', { count: 'exact' }),
        supabase
          .from('jobs')
          .select('type')
          .then(({ data }) => {
            const typeCount = new Map<string, number>();
            data?.forEach(job => {
              typeCount.set(job.type, (typeCount.get(job.type) || 0) + 1);
            });
            return Array.from(typeCount.entries())
              .map(([type, count]) => ({ type, count }))
              .sort((a, b) => b.count - a.count);
          }),
        supabase
          .from('job_matches')
          .select('score')
          .then(({ data }) => {
            const scoreRanges = new Map<string, number>();
            data?.forEach(match => {
              const scoreRange = getScoreRange(match.score);
              scoreRanges.set(scoreRange, (scoreRanges.get(scoreRange) || 0) + 1);
            });
            return Array.from(scoreRanges.entries())
              .map(([score, count]) => ({ score, count }))
              .sort((a, b) => {
                const scoreOrder = ['90-100%', '80-89%', '70-79%', '<70%'];
                return scoreOrder.indexOf(a.score) - scoreOrder.indexOf(b.score);
              });
          }),
        getMatchTrend(timeRange)
      ]);

      if (companies) {
        const stats: CompanyStats = {
          totalCompanies: companies.length,
          activeCompanies: companies.filter((c: Company) => c.jobs.length > 0).length,
          totalJobs: companies.reduce((acc: number, c: Company) => acc + c.jobs.length, 0),
          totalCandidates: totalCandidates || 0,
          totalMatches: companies.reduce((acc: number, c: Company) =>
            acc + c.jobs.reduce((matchAcc: number, j: Job) => matchAcc + j.matches.length, 0), 0),
          jobsByType: jobTypes || [],
          matchesByScore: matchScores || [],
          matchTrend
        };

        setCompanyStats({
          ...stats,
          totalApplications: totalApplications || 0
        });
      }
    } catch (error) {
      console.error('Error fetching comprehensive statistics:', error);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const getMatchTrend = async (range: TimeRange): Promise<{ date: string; matches: number }[]> => {
    const { data } = await supabase
      .from('matches')
      .select('created_at')
      .gte('created_at', getTimeRangeDate(range))
      .order('created_at');

    const trend = new Map<string, number>();
    data?.forEach((match) => {
      const date = new Date(match.created_at).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      trend.set(date, (trend.get(date) || 0) + 1);
    });

    return Array.from(trend.entries())
      .map(([date, matches]) => ({ date, matches }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const getTimeRangeDate = (range: TimeRange): string => {
    const now = new Date();
    switch (range) {
      case '24h':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      case '90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
    }
  };

  const getScoreRange = (score: number): string => {
    if (score >= 90) return '90-100%';
    if (score >= 80) return '80-89%';
    if (score >= 70) return '70-79%';
    return '<70%';
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <BarChart3 className="h-8 w-8 animate-pulse text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-5 w-5" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Statistics</h1>
        <Select value={timeRange} onValueChange={(value: TimeRange) => setTimeRange(value)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24 Hours</SelectItem>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="90d">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Companies</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(companyStats?.totalCompanies || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {companyStats?.activeCompanies} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(companyStats?.totalJobs || 0)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Candidates</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(companyStats?.totalCandidates || 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Matches</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(companyStats?.totalMatches || 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Applications</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(companyStats?.totalApplications || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Jobs by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={companyStats?.jobsByType}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ type, percent }) => `${type} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {companyStats?.jobsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 50%)`} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [value, 'Count']}
                    labelFormatter={(label) => `Job Type: ${label}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Match Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={companyStats?.matchesByScore}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="score" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [value, 'Matches']}
                    labelFormatter={(label) => `Score Range: ${label}`}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Match Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={companyStats?.matchTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [value, 'Matches']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line type="monotone" dataKey="matches" stroke="hsl(var(--primary))" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
